import { Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { SelectItem } from '../../../shared';
import { LoggingService } from '../../../shared/services/logging';
import * as fromScheduledDates from '../../../lookups/store/schedule-date.selectors';
import { map, takeWhile, withLatestFrom } from 'rxjs/operators';
import moment from 'moment';
import { ScheduledDate } from '../../../lookups';
import { SendJournalEntries } from '../../store';
import * as fromShared from '../../../shared';
import * as fromSettings from '../../../company-setting';

@Component({
    selector: 'ef-send-journal-entry-container',
    templateUrl: './send-journal-entry-container.component.html',
    styleUrls: ['./send-journal-entry-container.component.scss'],
    standalone: false
})
export class SendJournalEntryContainerComponent implements OnInit, OnDestroy {
    process$: Observable<fromShared.Process>;
    payPeriodDatesSelectItems$: Observable<SelectItem[]>;
    payPeriodDates$: Observable<ScheduledDate[]>;
    externalIntegration$: Observable<boolean>;
    private SendJournalEntries = new ReplaySubject<string>(1);
    public FilterChanged = new ReplaySubject<{
        PayPeriodEndDate: string;
        LaborAccrualDate: Date;
        LeaveAccrualDate: Date;
    }>(1);
    private active = true;
    constructor(private store: Store, private log: LoggingService) {}

    ngOnInit(): void {
        this.store.dispatch(
            fromSettings.getCompanySetting({
                SettingType: 'ExternalEmployeeSource'
            })
        );
        this.setupObservables();
        this.setupSubscriptions();
    }
    ngOnDestroy(): void {
        this.active = false;
    }
    setupObservables() {
        this.log.verbose({
            type: 'SendJournalEntryContainerComponent: setupObservables'
        });
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));
        this.externalIntegration$ = this.store.pipe(
            select(
                fromSettings.getSettingBooleanValue('ExternalEmployeeSource')
            )
        );
        this.payPeriodDates$ = this.store.pipe(
            select(
                fromScheduledDates.selectScheduledDateType({
                    ScheduledDateType: 'PPE'
                })
            )
        );
        this.payPeriodDatesSelectItems$ = this.payPeriodDates$.pipe(
            map(dates =>
                dates.map(d => ({
                    label: `${moment(d.ScheduledStartDate).format(
                        'MM/DD/YY'
                    )} - ${moment(d.ScheduledEndDate).format('MM/DD/YY')} ${
                        d.ScheduledDatePurpose
                    }`,
                    value: d.ScheduledDateID
                }))
            )
        );
    }
    setupSubscriptions() {
        this.log.verbose({
            type: 'SendJournalEntryContainerComponent: setupSubscriptions'
        });
        this.SendJournalEntries.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.FilterChanged),
            withLatestFrom(this.payPeriodDates$)
        ).subscribe(([[journalEntryType, filter], payPeriods]) => {
            this.log.verbose({
                type: 'SendJournalEntryContainerComponent:SendJournalEntries:subscribe',
                message: 'Sending Journal Entries',
                data: {
                    filter,
                    journalEntryType,
                    payPeriods
                }
            });
            const pped = payPeriods.find(
                x => x.ScheduledDateID === filter.PayPeriodEndDate
            );
            const usePayPeriodEnd =
                journalEntryType === 'labor' ||
                journalEntryType === 'govconpay';
            this.store.dispatch(
                new SendJournalEntries({
                    EndDate: usePayPeriodEnd
                        ? pped?.ScheduledEndDate ?? new Date()
                        : filter.LaborAccrualDate,
                    ReversalDate: filter.LeaveAccrualDate,
                    JournalType: journalEntryType
                })
            );
        });
    }
    onFilterChanged($event: {
        PayPeriodEndDate: string;
        LaborAccrualDate: Date;
        LeaveAccrualDate: Date;
    }) {
        this.log.verbose({
            type: 'SendJournalEntryContainerComponent',
            message: 'filterChanged',
            data: $event
        });
        this.FilterChanged.next($event);
    }
    onSendPayPeriodLabor() {
        this.log.verbose({
            type: 'SendJournalEntryContainerComponent',
            message: 'onSendPayPeriodLabor'
        });
        //Send Labor Journal Entry
        this.SendJournalEntries.next('labor');
    }
    onSendAccruals() {
        this.log.verbose({
            type: 'SendJournalEntryContainerComponent',
            message: 'onSendAccruals'
        });
        //Send Accrual Journal Entry
        this.SendJournalEntries.next('accrual');
        //Send Reversal Journal Entry
        this.SendJournalEntries.next('reversal');
    }
    onSendLeaveAccruals() {
        this.log.verbose({
            type: 'SendJournalEntryContainerComponent',
            message: 'onSendLeaveAccruals'
        });
        //Send Leave Accrual Journal Entry
        this.SendJournalEntries.next('leave');
    }
    onSendGovConPayPayroll() {
        this.log.verbose({
            type: 'SendJournalEntryContainerComponent',
            message: 'onSendGovConPayPayroll'
        });
        this.SendJournalEntries.next('govconpay');
    }
}
